import React, {
  useId,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  ArrowLeft,
  Send,
  Pen,
  ShieldCheck,
  Printer,
  History,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  DropdownField,
  Label,
} from '@roger/ui-components';
import {
  GenericOrderForm,
  type GenericOrderFormHandle,
} from './GenericOrderForm';
import { CMS485OrderForm } from './CMS485OrderForm';
import { PHOOrderForm } from './PHOOrderForm';
import { AutoSaveComments } from '../intake/shared';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
} from '../task/TaskHeaderControls';

import { Loader2 } from 'lucide-react';
import {
  signTask,
  approveTask,
  breakTaskSignatures,
  getOrder,
  getOrderPdf,
  getTaskSignatures,
  getAvailableClinicians,
  updateOrder,
  updateEpisodeAssignments,
  updateTaskNotes,
} from '../../services/api';
import {
  getTaskDocumentsTasksTaskIdDocumentsGet,
  getTaskDocumentUrlTasksTaskIdDocumentsDocumentIdUrlGet,
  listAgencyClinicPhysiciansAgencyClinicsAgencyClinicIdPhysiciansGet as listClinicPhysicians,
} from '../../services/api/sdk.gen';
import type {
  OrderResponse,
  TaskSignatureResponse,
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { usePdfExport, type PdfSource } from '../../hooks/usePdfExport';
import { useToast } from '../../hooks/useToastContext';
import { formatDate, useTaskPermissions } from '../task/TaskDetailPanel';
import { AttestationDialog } from '../signing/AttestationDialog';
import type { ESignatureCredentialInput } from '../signing/esignatureCredentialApi';
import { formatPhysicianName } from '../../utils/formatting';
import { cliniciansToSelectOptions } from '../../utils/clinicianUtils';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { AutoSavePill } from '../AutoSavePill';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';

interface OrderDetailPanelProps {
  orderId: string;
  patientId: string;
  onBack?: () => void;
  onVisitUpdated?: (visit: OrderResponse) => void;
}

type AssignmentField = 'clinic_physician_id' | 'case_manager_id';
type AssignmentState = Record<AssignmentField, string | null>;
type AssignmentSavingState = Record<AssignmentField, boolean>;

export const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
  orderId,
  patientId,
  onBack,
  onVisitUpdated,
}) => {
  const user = useStore(state => state.user);
  const { error: showError } = useToast();
  const { exportPdf, exportingId } = usePdfExport();
  const physicianFieldId = useId();
  const caseManagerFieldId = useId();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [visit, setVisit] = useState<OrderResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [cms485PdfUrl, setCms485PdfUrl] = useState<string | null>(null);
  const [cms485PdfLoading, setCms485PdfLoading] = useState(false);
  const genericFormRef = useRef<GenericOrderFormHandle>(null);
  // Mirrors the active editable form's autosave state so the pill can sit in a
  // shared sticky overlay (always visible) instead of scrolling away in the form
  // body. Reset on order switch so a prior order's "Saved" can't linger.
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  // Mirrors the active form's edit counter so the pill's debounce progress bar
  // restarts in sync with each keystroke that resets the autosave timer.
  const [saveChangeToken, setSaveChangeToken] = useState(0);
  useEffect(() => {
    setSaveStatus('idle');
    setSaveChangeToken(0);
  }, [orderId]);
  const handleSaveStatusChange = useCallback(
    (status: AutoSaveStatus, changeToken: number) => {
      setSaveStatus(status);
      setSaveChangeToken(changeToken);
    },
    []
  );
  const [pendingAssignments, setPendingAssignments] = useState<AssignmentState>(
    {
      clinic_physician_id: null,
      case_manager_id: null,
    }
  );
  const [savingAssignments, setSavingAssignments] =
    useState<AssignmentSavingState>({
      clinic_physician_id: false,
      case_manager_id: false,
    });
  const savingAssignmentsRef = useRef<AssignmentSavingState>({
    clinic_physician_id: false,
    case_manager_id: false,
  });
  const assignmentRequestIdsRef = useRef<Record<AssignmentField, number>>({
    clinic_physician_id: 0,
    case_manager_id: 0,
  });

  const setPendingAssignment = (
    field: AssignmentField,
    value: string | null
  ) => {
    setPendingAssignments(current => {
      return { ...current, [field]: value };
    });
  };

  const setAssignmentSaving = (field: AssignmentField, value: boolean) => {
    const next = { ...savingAssignmentsRef.current, [field]: value };
    savingAssignmentsRef.current = next;
    setSavingAssignments(next);
  };

  const hasSavingAssignment = () =>
    Object.values(savingAssignmentsRef.current).some(Boolean);

  const getAssignmentValue = (order: OrderResponse, field: AssignmentField) =>
    field === 'clinic_physician_id'
      ? (order.clinic_physician_id ?? null)
      : (order.episode_case_manager_id ?? null);

  const applyAssignmentToOrder = (
    order: OrderResponse,
    field: AssignmentField,
    value: string
  ): OrderResponse =>
    field === 'clinic_physician_id'
      ? { ...order, clinic_physician_id: value }
      : { ...order, episode_case_manager_id: value };

  // --- Data fetching ---

  const fetchData = async () => {
    try {
      setLoading(true);

      const [visitData, signaturesData] = await Promise.all([
        getOrder({ path: { order_id: orderId } }),
        getTaskSignatures({ path: { task_id: orderId } }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data as unknown as OrderResponse);
        onVisitUpdated?.(visitData.data as unknown as OrderResponse);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error fetching order data:', err);
      showError('Load Failed', 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  // Saving the order's notes auto-advances a Not Started order to Saved on
  // the backend. Reflect that returned status locally instead of refetching,
  // which would reset the notes textarea mid-typing (useAutoSave re-syncs on
  // prop change).
  const saveOrderNotes = async (value: string) => {
    const res = await updateTaskNotes({
      path: { task_id: orderId },
      body: { notes: value.trim() || null },
    });
    const nextStatus = res.data?.status;
    if (nextStatus) {
      setVisit(prev => (prev ? { ...prev, status: nextStatus } : prev));
    }
  };

  // GenericOrderForm autosaves the order itself and hands back the fresh row.
  // Sync it into panel state and bubble it up WITHOUT calling fetchData(): a
  // full refetch flips `loading`, which unmounts the whole panel (see the
  // `if (loading)` guard below) and would drop the form's in-progress edits
  // mid-autosave. An order-data edit doesn't touch signatures, so there's
  // nothing else to reload here.
  const handleGenericOrderSaved = useCallback(
    (updatedOrder: OrderResponse) => {
      setVisit(updatedOrder);
      onVisitUpdated?.(updatedOrder);
    },
    [onVisitUpdated]
  );

  const isReadonly = !EDITABLE_STATUSES.has(visit?.status ?? 'not_started');

  useEffect(() => {
    fetchData();
  }, [orderId]);

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        const [physRes, cmRes] = await Promise.all([
          visit?.patient_clinic_id
            ? listClinicPhysicians({
                path: { agency_clinic_id: visit.patient_clinic_id },
              })
            : Promise.resolve({ data: [] }),
          getAvailableClinicians({
            query: visit?.agency_clinic_id
              ? { clinic_id: visit.agency_clinic_id }
              : {},
          }),
        ]);
        if (physRes.data) {
          setPhysicians(physRes.data as ClinicPhysicianResponse[]);
        }
        if (cmRes.data) {
          setCaseManagers(cmRes.data.filter(c => c.is_case_manager));
        }
      } catch (err) {
        console.error('Failed to fetch clinicians:', err);
        showError(
          'Load Failed',
          'Failed to load physicians and case managers.'
        );
      }
    };
    if (!loading) fetchClinicians();
  }, [loading, visit?.agency_clinic_id, visit?.patient_clinic_id, showError]);

  // Load the signed CMS-485 PDF (persisted to the task at sign time) for readonly mode
  useEffect(() => {
    if (visit?.order_type !== 'CMS_485' || !isReadonly || loading) return;

    let cancelled = false;
    const loadPdf = async () => {
      setCms485PdfLoading(true);
      try {
        const docsRes = await getTaskDocumentsTasksTaskIdDocumentsGet({
          path: { task_id: orderId },
        });
        if (docsRes.error) throw new Error('Failed to load task documents');
        const cms485Doc = (docsRes.data ?? []).find(
          d => d.document_type === 'cms_485'
        );
        if (!cms485Doc) {
          if (!cancelled) setCms485PdfUrl(null);
          return;
        }
        const urlRes =
          await getTaskDocumentUrlTasksTaskIdDocumentsDocumentIdUrlGet({
            path: { task_id: orderId, document_id: cms485Doc.id },
          });
        if (urlRes.error) throw new Error('Failed to load CMS-485 URL');
        if (!cancelled) setCms485PdfUrl(urlRes.data?.url ?? null);
      } catch (err) {
        console.error('Failed to load CMS-485 PDF:', err);
        if (!cancelled) setCms485PdfUrl(null);
      } finally {
        if (!cancelled) setCms485PdfLoading(false);
      }
    };
    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [visit?.order_type, visit?.status, isReadonly, loading, orderId]);

  const handleAssignmentChange = async (
    field: AssignmentField,
    value: string
  ) => {
    if (!visit) return;

    if (hasSavingAssignment() || value === getAssignmentValue(visit, field)) {
      return;
    }

    const requestId = assignmentRequestIdsRef.current[field] + 1;
    assignmentRequestIdsRef.current[field] = requestId;
    setPendingAssignment(field, value);
    setAssignmentSaving(field, true);

    try {
      const result =
        field === 'clinic_physician_id'
          ? await updateOrder({
              path: { order_id: visit.id },
              body: { clinic_physician_id: value },
            })
          : await updateEpisodeAssignments({
              path: { episode_id: visit.episode_id },
              body: { [field]: value },
            });
      if (result.error) {
        console.error('Failed to update assignment:', result.error);
        showError(
          'Update Failed',
          'Failed to update assignment. Please try again.'
        );
        return;
      }

      setVisit(current =>
        current ? applyAssignmentToOrder(current, field, value) : current
      );
      setPendingAssignment(field, null);

      try {
        await refreshVisitAndSignatures({ field, value });
      } catch (refreshErr) {
        console.error(
          'Failed to refresh order after assignment update:',
          refreshErr
        );
        showError(
          'Update Saved',
          'Assignment saved, but refreshed details could not be loaded. Please refresh if details look stale.'
        );
      }
    } catch (err) {
      console.error('Failed to update assignment:', err);
      showError(
        'Update Failed',
        'Failed to update assignment. Please try again.'
      );
    } finally {
      if (assignmentRequestIdsRef.current[field] === requestId) {
        setPendingAssignment(field, null);
        setAssignmentSaving(field, false);
      }
    }
  };

  // --- Derived permission state ---

  const { hasCaseManagerRole, canSign, canApprove, canBreakSignatures } =
    useTaskPermissions(user, visit);

  const hasRequiredAssignments =
    !!visit?.clinic_physician_id && !!visit?.episode_case_manager_id;

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const signatureSummary = useMemo(
    () =>
      activeSignatures.map(signature => {
        const metadata =
          (signature.signature_metadata as Record<string, unknown> | null) ??
          {};
        const displayName =
          (metadata.signer_display_name as string | undefined) ||
          (metadata.user_name as string | undefined) ||
          (metadata.user_email as string | undefined) ||
          'Unknown signer';
        const credentials = metadata.signer_credentials as
          | string
          | undefined
          | null;
        const role = metadata.signer_role as string | undefined;

        return {
          id: signature.id,
          role:
            role === 'case_manager'
              ? 'Case Manager'
              : role === 'clinician'
                ? 'Physician'
                : 'Signer',
          name: credentials ? `${displayName}, ${credentials}` : displayName,
        };
      }),
    [activeSignatures]
  );

  // Status gate removed per ROG-1942 — comments enabled for case managers at all
  // order statuses. Backend enforces role authorization via PUT /tasks/{id}/notes.
  const canEditNotes = hasCaseManagerRole;
  const hasPrimaryHeaderActions = canBreakSignatures || canSign || canApprove;
  const assignmentSaveInFlight = Object.values(savingAssignments).some(Boolean);

  // --- Refresh helpers ---

  const refreshVisitAndSignatures = async (expectedAssignment?: {
    field: AssignmentField;
    value: string;
  }): Promise<OrderResponse | null> => {
    const [visitData, signaturesData] = await Promise.all([
      getOrder({ path: { order_id: orderId } }),
      getTaskSignatures({ path: { task_id: orderId } }),
    ]);
    if (expectedAssignment) {
      if (visitData?.error) throw visitData.error;
      if (signaturesData?.error) throw signaturesData.error;
    }
    const refreshedVisit = visitData?.data
      ? (visitData.data as unknown as OrderResponse)
      : null;
    if (
      refreshedVisit &&
      expectedAssignment &&
      getAssignmentValue(refreshedVisit, expectedAssignment.field) !==
        expectedAssignment.value
    ) {
      throw new Error('Refreshed order did not include updated assignment.');
    }
    if (refreshedVisit) {
      setVisit(refreshedVisit);
      onVisitUpdated?.(refreshedVisit);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
    return refreshedVisit;
  };

  // --- Action handlers ---

  const orderAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('order', visit?.order_type ?? null)
    );
  }, [attestations, visit?.order_type]);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyDialogFrameRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (historyDialogFrameRef.current !== null) {
        cancelAnimationFrame(historyDialogFrameRef.current);
      }
    };
  }, []);

  const handleSignAndSubmit = async () => {
    if (hasSavingAssignment()) return;

    // Flush any pending debounced auto-save so the backend sees the latest
    // field values before the sign call. CMS-485 and PHO own their own
    // pre-sign flow elsewhere; everything else (generic + summary forms)
    // routes through whichever form ref is currently mounted.
    const editableFormHandle =
      visit?.order_type === 'CMS_485' ||
      visit?.order_type === 'POST_HOSPITAL_ORDER'
        ? null
        : genericFormRef.current;
    if (editableFormHandle) {
      const outcome = await editableFormHandle.flush();
      // A non-'saved' flush means the latest edits did not persist: 'error'
      // already surfaced a toast from the form's onSave, and 'declined' leaves
      // the inline validation errors visible. Either way, do not sign stale
      // data or close out of the form.
      if (outcome !== 'saved') return;
      const isValid = await editableFormHandle.validate();
      if (!isValid) return;
    }
    setSignAttestationOpen(true);
  };

  const doSignAndSubmit = async (credential: ESignatureCredentialInput) => {
    if (!orderAttestation) return;

    try {
      setSigningLoading(true);
      const result = await signTask({
        path: { task_id: orderId },
        body: { ...credential, attestation_version: orderAttestation.version },
      });
      if (result.error) {
        console.error('Error signing order:', result.error);
        showError(
          'Sign Failed',
          getBusinessErrorMessage(result) ??
            'Failed to sign and submit. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
      // Closed only on success. Leaving it open on failure keeps the entered
      // e-signature password, so a mistyped one can be corrected rather than
      // retyped from scratch on each of the five attempts before lockout.
      setSignAttestationOpen(false);
    } catch (err) {
      console.error('Error signing order:', err);
      showError('Sign Failed', 'Failed to sign and submit. Please try again.');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleApprove = async () => {
    if (hasSavingAssignment()) return;

    setApproveAttestationOpen(true);
  };

  const doApprove = async (credential: ESignatureCredentialInput) => {
    if (!orderAttestation) return;

    try {
      setApprovingLoading(true);
      const result = await approveTask({
        path: { task_id: orderId },
        body: { ...credential, attestation_version: orderAttestation.version },
      });
      if (result.error) {
        console.error('Error approving order:', result.error);
        showError(
          'Approve Failed',
          getBusinessErrorMessage(result) ??
            'Failed to complete order. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
      setApproveAttestationOpen(false);
    } catch (err) {
      console.error('Error approving order:', err);
      showError(
        'Approve Failed',
        'Failed to complete order. Please try again.'
      );
    } finally {
      setApprovingLoading(false);
    }
  };

  const handleBreakSignatures = async () => {
    try {
      setEditingLoading(true);
      const result = await breakTaskSignatures({
        path: { task_id: orderId },
      });
      if (result.error) {
        console.error('Error unlocking order:', result.error);
        showError('Unlock Failed', 'Failed to unlock order. Please try again.');
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error unlocking order:', err);
      showError('Unlock Failed', 'Failed to unlock order. Please try again.');
    } finally {
      setEditingLoading(false);
    }
  };

  // --- PDF export (opens in new tab) ---

  const handleExportPdf = () => {
    if (!orderId) return;
    // GET /orders/{id}/pdf is now canonical: the backend resolves the right
    // artifact by order type (signed CMS-485 form, styled discharge/transfer
    // summary, or generic metadata), so this client no longer branches.
    void exportPdf(
      orderId,
      async (): Promise<PdfSource | null> => {
        const response = await getOrderPdf({
          path: { order_id: orderId },
        });
        return response.data
          ? {
              blob: new Blob([response.data as BlobPart], {
                type: 'application/pdf',
              }),
            }
          : null;
      },
      'order PDF',
      {
        // Mirror handleSignAndSubmit: flush any pending debounced auto-save so
        // the exported PDF reflects the latest field values. A non-'saved'
        // outcome means the edits did not persist ('error' already toasted from
        // the form's onSave, 'declined' leaves inline validation visible), so
        // abort the export rather than print stale, server-persisted data.
        preflight: async (): Promise<boolean> => {
          const editableFormHandle =
            visit?.order_type === 'CMS_485' ||
            visit?.order_type === 'POST_HOSPITAL_ORDER'
              ? null
              : genericFormRef.current;
          if (!editableFormHandle) return true;
          return (await editableFormHandle.flush()) === 'saved';
        },
      }
    );
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Order not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center gap-2'>
              <span className='min-w-0 flex-1 truncate text-sm font-medium text-foreground'>
                {visit.order_type_display_name}
              </span>
            </div>
            <div className='text-xs text-muted-foreground'>
              Order date: {formatDate(visit.order_date)}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <>
              {/* Status Badge — signature summary folds into its tooltip */}
              <TaskWorkflowStatusBadge
                status={visit.status}
                signatures={signatures}
              />
            </>
          }
          primaryActions={
            hasPrimaryHeaderActions ? (
              <>
                {/* Unlock & Edit */}
                {canBreakSignatures && (
                  <Button
                    onClick={handleBreakSignatures}
                    disabled={editingLoading}
                    size='sm'
                    variant='outline'
                    className='flex items-center gap-1.5'
                  >
                    {editingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Pen className='h-4 w-4' />
                        {visit?.status === 'completed' ||
                        visit?.status === 'exported'
                          ? 'Reopen'
                          : 'Unlock & Edit'}
                      </>
                    )}
                  </Button>
                )}

                {/* Sign & Submit */}
                {canSign && (
                  <Button
                    onClick={handleSignAndSubmit}
                    disabled={
                      signingLoading ||
                      assignmentSaveInFlight ||
                      !hasRequiredAssignments ||
                      attestationsLoading ||
                      !orderAttestation
                    }
                    title={
                      !hasRequiredAssignments
                        ? 'Assign physician and case manager first'
                        : undefined
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {signingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Signing...
                      </>
                    ) : (
                      <>
                        <Send className='h-4 w-4' />
                        Sign & Submit
                      </>
                    )}
                  </Button>
                )}

                {/* Sign & Complete */}
                {canApprove && (
                  <Button
                    onClick={handleApprove}
                    disabled={
                      approvingLoading ||
                      assignmentSaveInFlight ||
                      !hasRequiredAssignments ||
                      attestationsLoading ||
                      !orderAttestation
                    }
                    title={
                      !hasRequiredAssignments
                        ? 'Assign physician and case manager first'
                        : undefined
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {approvingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Completing...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className='h-4 w-4' />
                        Sign & Complete
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : undefined
          }
          secondaryActions={[
            {
              label:
                exportingId === orderId
                  ? 'Opening print view...'
                  : 'Print view',
              icon:
                exportingId === orderId ? (
                  <div
                    className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
                    aria-hidden='true'
                  />
                ) : (
                  <Printer className='h-4 w-4 mr-2' aria-hidden='true' />
                ),
              onSelect: handleExportPdf,
              disabled: exportingId === orderId,
            },
            {
              label: 'View history',
              // Defer opening to the next frame so the Actions dropdown finishes
              // unmounting (and releases its body pointer-events lock) before the
              // dialog mounts — otherwise the page is left unclickable.
              icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                if (historyDialogFrameRef.current !== null) {
                  cancelAnimationFrame(historyDialogFrameRef.current);
                }
                historyDialogFrameRef.current = requestAnimationFrame(() => {
                  historyDialogFrameRef.current = null;
                  setHistoryOpen(true);
                });
              },
            },
          ]}
        />
      </div>

      {/* Assignment controls while editable; signer history once readonly. */}
      <div className='flex items-center gap-4 px-5 py-2 border-b bg-muted/10'>
        {isReadonly ? (
          signatureSummary.length > 0 ? (
            signatureSummary.map(signature => (
              <div key={signature.id} className='flex items-center gap-2'>
                <Label className='text-xs text-muted-foreground whitespace-nowrap'>
                  {signature.role} Signer
                </Label>
                <span className='text-sm'>{signature.name}</span>
              </div>
            ))
          ) : null
        ) : (
          <>
            <div className='w-48'>
              <DropdownField
                label='Physician'
                htmlFor={physicianFieldId}
                variant='formSelect'
                sortOptions
                fieldSize='sm'
                placeholder='Select physician'
                options={physicians.map(p => ({
                  value: p.id,
                  label: formatPhysicianName(p),
                }))}
                value={
                  pendingAssignments.clinic_physician_id ??
                  visit.clinic_physician_id ??
                  ''
                }
                onChange={v => handleAssignmentChange('clinic_physician_id', v)}
                disabled={assignmentSaveInFlight}
              />
            </div>
            <div className='w-48'>
              <DropdownField
                label='Episode Case Manager'
                htmlFor={caseManagerFieldId}
                variant='formSelect'
                sortOptions
                fieldSize='sm'
                placeholder='Select case manager'
                options={cliniciansToSelectOptions(caseManagers)}
                value={
                  pendingAssignments.case_manager_id ??
                  visit.episode_case_manager_id ??
                  ''
                }
                onChange={v => handleAssignmentChange('case_manager_id', v)}
                disabled={assignmentSaveInFlight}
              />
            </div>
          </>
        )}
      </div>

      {/* Save status pill: a zero-height sticky overlay above the scrollable
          body, floating over its top-right. Only the editable order forms
          generic report status; CMS 485 / PHO use a different save
          flow. Zero height means showing/hiding the pill never shifts layout,
          and the sticky positioning keeps it in view as the form scrolls. */}
      {!isReadonly && (
        <div className='pointer-events-none sticky top-0 z-20 flex h-0 shrink-0 justify-end px-5'>
          <AutoSavePill
            status={saveStatus}
            debounceMs={1500}
            changeToken={saveChangeToken}
          />
        </div>
      )}

      {/* Body */}
      <div className='flex-1 overflow-auto p-4'>
        {isReadonly ? (
          visit.order_type === 'CMS_485' ? (
            <div className='space-y-4'>
              <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
                {cms485PdfLoading ? (
                  <div className='h-full w-full flex items-center justify-center border rounded-md'>
                    <Loader2 className='h-8 w-8 animate-spin' />
                  </div>
                ) : cms485PdfUrl ? (
                  <div className='h-full w-full border rounded-md overflow-hidden bg-muted/10'>
                    <iframe
                      src={cms485PdfUrl}
                      title='CMS 485 Preview'
                      className='h-full w-full'
                    />
                  </div>
                ) : (
                  <div className='h-full w-full flex items-center justify-center border rounded-md text-muted-foreground'>
                    <p>Failed to generate CMS-485 preview.</p>
                  </div>
                )}
              </div>
              {canEditNotes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              ) : visit.notes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <div className='whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {visit.notes}
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          ) : (
            <Card>
              <CardContent className='p-4 space-y-4'>
                {(() => {
                  const data = visit.order_data as Record<
                    string,
                    string
                  > | null;
                  const orderDate = visit.order_date || data?.order_date;
                  const orderTime = visit.order_time || data?.order_time;
                  const orderText = visit.order_text || data?.order_text;
                  const orderSummary =
                    visit.order_summary || data?.order_summary;
                  return (
                    <>
                      <div className='grid grid-cols-2 gap-4 text-sm'>
                        <div>
                          <span className='font-medium text-muted-foreground'>
                            Order Date
                          </span>
                          <p>{orderDate}</p>
                        </div>
                        {orderTime && (
                          <div>
                            <span className='font-medium text-muted-foreground'>
                              Order Time
                            </span>
                            <p>{orderTime}</p>
                          </div>
                        )}
                      </div>
                      {orderText && (
                        <div>
                          <span className='text-sm font-medium text-muted-foreground'>
                            Order Text
                          </span>
                          <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                            {orderText}
                          </div>
                        </div>
                      )}
                      {orderSummary && (
                        <div>
                          <span className='text-sm font-medium text-muted-foreground'>
                            Order Summary
                          </span>
                          <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                            {orderSummary}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {canEditNotes ? (
                  <div>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      className='mt-1'
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </div>
                ) : visit.notes ? (
                  <div>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {visit.notes}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )
        ) : (
          <>
            {visit.order_type === 'CMS_485' ? (
              <>
                <CMS485OrderForm visit={visit} patientId={patientId} />
                <Card className='mt-4'>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              </>
            ) : visit.order_type === 'POST_HOSPITAL_ORDER' ? (
              <>
                <PHOOrderForm visit={visit} patientId={patientId} />
                <Card className='mt-4'>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <GenericOrderForm
                  ref={genericFormRef}
                  visit={visit}
                  onSave={handleGenericOrderSaved}
                  onSaveStatusChange={handleSaveStatusChange}
                />
                {canEditNotes && (
                  <Card className='mt-4'>
                    <CardContent className='space-y-2'>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Notes
                      </span>
                      <AutoSaveComments
                        value={visit.notes ?? ''}
                        onSave={saveOrderNotes}
                        placeholder='Add notes for this order...'
                      />
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel='Sign & Submit'
        onConfirm={credential => void doSignAndSubmit(credential)}
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approvingLoading}
        actionLabel='Approve & Complete'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={orderId}
      />
    </div>
  );
};
